import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { setSimulatedFullscreenElement } from "../../../common/util/fullscreenPortalTarget";

interface WebkitFullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface WebkitFullscreenDocument extends Document {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

interface UseFullscreenReturn {
  isFullscreen: boolean;
  toggleFullscreen: (elementRef: RefObject<HTMLElement | null>) => void;
}

type FullscreenMode = "none" | "real" | "simulated";
const FALLBACK_TIMEOUT_MS = 500;

export const useFullscreen = (): UseFullscreenReturn => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modeRef = useRef<FullscreenMode>("none");
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current !== null) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const enterSimulated = useCallback(
    (element: Element) => {
      clearFallbackTimer();
      if (modeRef.current === "real") return;
      modeRef.current = "simulated";
      setSimulatedFullscreenElement(element);
      setIsFullscreen(true);

      window.scrollTo(0, 1);
    },
    [clearFallbackTimer],
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as WebkitFullscreenDocument;
      const fullscreenElement = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;

      if (fullscreenElement !== null) {
        clearFallbackTimer();
        modeRef.current = "real";
        setIsFullscreen(true);
        return;
      }

      // Native fullscreen ended — via our own exitFullscreen() call, or the
      // browser's own UI (Esc key, swipe back). A null fullscreenElement is
      // also the steady state while simulated mode is active, so only react
      // here when we were actually in "real" mode; simulated exit is handled
      // directly in toggleFullscreen instead.
      if (modeRef.current === "real") {
        modeRef.current = "none";
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
    };
  }, [clearFallbackTimer]);

  useEffect(() => clearFallbackTimer, [clearFallbackTimer]);

  useEffect(
    () => () => {
      if (modeRef.current === "simulated") {
        setSimulatedFullscreenElement(null);
      }
    },
    [],
  );

  const toggleFullscreen = useCallback(
    (elementRef: RefObject<HTMLElement | null>) => {
      const doc = document as WebkitFullscreenDocument;

      /**
       * There are two fullscreen modes:
       * Real - via the FullScreen API
       * Simulated - if the FullScreen API is not supported (mobile devices) or fails. This will open the TreeView in a fullscreen modal
       *
       */

      if (modeRef.current === "real") {
        if (doc.exitFullscreen) {
          void doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          void doc.webkitExitFullscreen();
        }
        return;
      }

      if (modeRef.current === "simulated") {
        clearFallbackTimer();
        modeRef.current = "none";
        setSimulatedFullscreenElement(null);
        setIsFullscreen(false);
        return;
      }

      const element = elementRef.current as WebkitFullscreenElement | null;
      if (!element) return;

      let requestFullscreen: (() => Promise<void> | void) | undefined;
      if (element.requestFullscreen) {
        requestFullscreen = () => element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        requestFullscreen = () => element.webkitRequestFullscreen?.();
      }

      if (!requestFullscreen) {
        enterSimulated(element);
        return;
      }

      clearFallbackTimer();
      fallbackTimerRef.current = setTimeout(() => {
        fallbackTimerRef.current = null;
        if (modeRef.current !== "real") enterSimulated(element);
      }, FALLBACK_TIMEOUT_MS);

      void Promise.resolve(requestFullscreen()).catch(() => {
        if (modeRef.current !== "real") enterSimulated(element);
      });
    },
    [clearFallbackTimer, enterSimulated],
  );

  return { isFullscreen, toggleFullscreen };
};
